import { Injectable, EventEmitter, PLATFORM_ID, Inject } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { environment } from 'src/environments/environment';

@Injectable({
   providedIn: 'root',
})
export class AuthService {
  checking = false
  private token: string = ''
  private role: string = ''
  private userId: string = ''
  private is_onboarding_trial_claimed: boolean = false
  private email: string = ''
  private firstname: string = ''
  private lastname: string = ''
  private mobile: string = ''
  private viewedCollections: string[] = []
  private referralLink: string = ''
  private musaffaInvestmentOptout: boolean = false;
  public roleUpdatedEvent = new EventEmitter<boolean>();

  // ✅ Onboarding trial claim observable
  private isTrialClaimedSubject = new BehaviorSubject<boolean>(false);
  public isTrialClaimed$ = this.isTrialClaimedSubject.asObservable();

  private modalRef: NgbModalRef | undefined;
  private isBrowser: boolean;
  isLoggedInObserver = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedInObserver.asObservable();
  constructor(
    private cookie:CookieService,
    private modalService: NgbModal,
    private apiService: ApiService,
    @Inject(PLATFORM_ID) private platformId: Object
    ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
    this.token = localStorage.getItem('token') || ''
    this.userId = this.filterNull(localStorage.getItem('userId'))
    this.email = this.filterNull(localStorage.getItem('email'))
    this.firstname = this.filterNull(localStorage.getItem('firstname'))
    this.lastname = this.filterNull(localStorage.getItem('lastname'))
    this.mobile = this.filterNull(localStorage.getItem('mobile'))
    this.role = this.filterNull(localStorage.getItem('role'))
    this.referralLink = this.filterNull(localStorage.getItem('referralLink'));

    this.musaffaInvestmentOptout = localStorage.getItem('musaffaInvestmentOptout') === 'true';

    if (this.token) {
      this.checking = true
    } else {
      this.checking = false
    }
   }
  }

  async logout(callApi: boolean = true) {
    if(callApi) {
      try {
        this.apiService.logout(this.token);
      } catch (error) {
        console.error('Error logging out:', error);
      }
    }
   this.token = ''
   this.userId = ''
   this.role = ''
   this.email = ''
   this.firstname = ''
   this.lastname = ''
   this.mobile = ''
   this.checking = false;
   if(this.isBrowser) {
      this.cookie.removeAll();
      const disclaimerAccepted = localStorage.getItem('disclaimerAccepted')
      localStorage.clear()
      if (disclaimerAccepted === 'true') {
         localStorage.setItem('disclaimerAccepted', 'true')
      }
      const megaLaunchCampaignPopup = sessionStorage.getItem('megaLaunchCampaignPopup')
      sessionStorage.clear()
      if (megaLaunchCampaignPopup === 'true') {
         sessionStorage.setItem('megaLaunchCampaignPopup', 'true')
      }
      this.roleUpdatedEvent.emit(true)
      this.isLoggedInObserver.next(false)
      const redirectUrl = encodeURIComponent(window.location.href);
      // Redirect to SSO logout - this will navigate away immediately
      window.location.href = `${environment.authUrl}/auth/sso/logout?redirect_to=${redirectUrl}`;
   }
 }

 
   public async openAuthPopup(
      action: string | null = null,
      dismissRedirect: string | null = null,
      formName: 'signUp' | 'signIn' = 'signIn',
      selectedSignUpUI: 'default' | 'waitlist' = 'default',
      signupHeaderText: string = 'Signup For Free',
      signupSubheaderText: string = 'Join thousands of halal investors. Make confident, Shariah-compliant decisions',
      loginHeaderText: string = 'Assalamu Alaykum',
      loginSubheaderText: string = 'Welcome back! Log in to access your halal stock insights and screening tools.',
      size: 'sm' | 'md' | 'lg'| 'xl' = 'xl'
   ) {
      if (this.modalService.hasOpenModals()) {
         return;
      }

      const { AuthPopupComponent } = await import('src/app/authentication/auth-popup/auth-popup.component');

      this.modalRef = this.modalService.open(AuthPopupComponent, {
         centered: true,
         scrollable: true,
         backdropClass: 'global-modal-backdrop',
         windowClass: 'rounded-border-popup',
         modalDialogClass: 'tw-w-full !tw-max-w-5xl',
         backdrop: !dismissRedirect ? true : 'static',
         size: '' 
      });

      if (action) {
         this.modalRef.componentInstance.action = action;
      }
      this.modalRef.componentInstance.dismissRedirect = dismissRedirect || null;
      this.modalRef.componentInstance.formName = formName;
      this.modalRef.componentInstance.selectedSignUpUI = selectedSignUpUI;
      this.modalRef.componentInstance.signupHeaderText = signupHeaderText;
      this.modalRef.componentInstance.signupSubheaderText = signupSubheaderText;
      this.modalRef.componentInstance.loginHeaderText = loginHeaderText;
      this.modalRef.componentInstance.loginSubheaderText = loginSubheaderText;
   }

   closeAuthPopup() {
      if (this.modalRef) {
         this.modalRef.close();
      }
   }

  async setToken(token: any): Promise<void> {
    this.token = token
    if (this.isBrowser) {
      localStorage.setItem('token', token)
    }
  }

  async setUserData(response: any): Promise<void> {
    var rolesArr = response['roles']?.map((obj:any)=>{
      obj.name
    })

    var rolesArr = response['authorities'].map(function(authority: any){
        return authority.name;
    }).join(",")

    this.userId = response['identifier']
    this.email = response['email']
    this.firstname = response['firstname']
    this.lastname = response['lastname']
    this.mobile = response['mobile']
    this.is_onboarding_trial_claimed = this.normalizeTrialValue(response['is_onboarding_trial_claimed']);
    this.syncOnboardingTrialClaimed();
    // console.log('[AuthService] User data received -> is_onboarding_trial_claimed:', response['is_onboarding_trial_claimed']);
    this.referralLink = response['referral_url']
    this.musaffaInvestmentOptout = response['musaffa_investment_optout']
    this.checking = true
    if (this.isBrowser) {
      localStorage.setItem('userId', this.userId || '')
      localStorage.setItem('email', this.email || '')
      localStorage.setItem('firstname', this.firstname || '')
      localStorage.setItem('lastname', this.lastname || '')
      localStorage.setItem('mobile', this.mobile || '')
      localStorage.setItem('referralLink', this.referralLink || '')
      localStorage.setItem('musaffaInvestmentOptout', JSON.stringify(this.musaffaInvestmentOptout))
    }
    this.setRole(rolesArr)
    if (this.isBrowser) {
      await this.cookie.put('_authorization', `Bearer ${this.token}`, {secure: false, httpOnly: false, sameSite: 'lax'})
      localStorage.setItem('cookie', 'true');
    }

    this.roleUpdatedEvent.emit(true)
    this.isLoggedInObserver.next(true)
  }

  storeToken(token:string){
    if (this.isBrowser) {
      localStorage.setItem('token', token)
    }
  }

  setLoggedIn(){
    if (this.isBrowser) {
      localStorage.removeItem('dataFilterCountry');
      localStorage.setItem('is_logged_in', '1')
    }
    this.isLoggedInObserver.next(true)
    this.roleUpdatedEvent.emit(true)
  }


  setRole(role:string)
  {
    this.role = role;
    if (this.isBrowser) {
      localStorage.setItem('role', this.role)
    }
    this.roleUpdatedEvent.emit(true)
  }

  setViewedCollections(symbols: [])
  {
   this.viewedCollections = symbols
  }

  addViewedCollection(symbol: string)
  {
   this.viewedCollections.push(symbol)
  }

  deleteViewedCollections()
  {
   this.viewedCollections = []
  }

  getViewedCollections()
  {
      return this.viewedCollections
  }

  getIsOnboardingTrialClaimed(): boolean {
    return this.is_onboarding_trial_claimed
  }

  getToken(): string {
    return this.token
  }

  getUserId(): string {
    if (this.isBrowser) {
      this.isLoggedInObserver.next(!!this.userId)
    }
    return this.userId
  }

  getEmail(): string {
    return this.email
  }

  getFirstName(): any {
    return this.firstname
  }

  getLastName(): any {
    return this.lastname
  }

  getMobile(): string {
    return this.mobile
  }

  getReferralLink(): string {
    return this.referralLink
  }

  getMusaffaInvestmentOptoutValue(): boolean {
    return this.musaffaInvestmentOptout
  }

  setMusaffaInvestmentOptout(value: boolean): void {
    this.musaffaInvestmentOptout = value;
  
    if (this.isBrowser) {
      localStorage.setItem( 'musaffaInvestmentOptout', JSON.stringify(value) );
    }
  }

  isLoggedIn(): boolean {
   return !!this.getUserId()

    // if(token)
    //   return true
    // var currentUser = await lastValueFrom(this.authFire.authState.pipe(first()))

    // return currentUser ? !currentUser.isAnonymous : false
  }

  getRole(): string {
    if (this.role) {
      let roles = this.role.split(',')
      return roles[roles.length - 1]
    } else {
      return ''
    }
  }

  checkRole(role:string):boolean
  {
    if(role == 'ANONYMOUS'){
      return !this.isLoggedIn()
    }
    else{
      if(this.isLoggedIn()){
        let roles = this.role?.split(',');
        return roles?.includes(role);
      }
      return false;
   }
   }

  private normalizeTrialValue(value: any): boolean {
    return value === true || value === 1 || value === '1';
  }

   // ✅ Keep localStorage and observable in sync
  private syncOnboardingTrialClaimed(): void {
    if (this.isBrowser) {
      localStorage.setItem('is_onboarding_trial_claimed', String(this.is_onboarding_trial_claimed));
    }
    this.isTrialClaimedSubject.next(this.is_onboarding_trial_claimed);
  }

  // ✅ Optional helper to manually update from other components
  public setOnboardingTrialClaimed(value: boolean): void {
    this.is_onboarding_trial_claimed = value;
    this.syncOnboardingTrialClaimed();
  }

  private filterNull(value: any): string {
    if (value === null || value === undefined || value === 'null' || value === 'undefined') {
      return '';
    }
    return String(value);
  }

  setSignupSource(source: string) {
    if (this.isBrowser) {
      sessionStorage.setItem('signupSource', source);
    }
  }

  getSignupSource(): string {
    if (this.isBrowser) {
      return sessionStorage.getItem('signupSource') || '';
    }
    return '';
  }

  clearSignupSource() {
    if (this.isBrowser) {
      sessionStorage.removeItem('signupSource');
    }
  }
}
